import {RefObject, useMemo} from 'react';
import {useFaceDetection, FacePosition} from './useFaceDetection';
import {useServerFaceDetection} from './useServerFaceDetection';
import {useLivenessModelsStore} from '../store';
import {canUseClientSideDetection} from '../utils/deviceCapabilities';
import {WebcamRefTypes} from '@chekin/guestapp-ui-kit';

interface UseAdaptiveFaceDetectionProps {
  videoRef?: RefObject<WebcamRefTypes>;
  forceLivenessMechanism?: 'AUTO' | 'CLIENT' | 'SERVER';
}

export const useAdaptiveFaceDetection = ({
  videoRef,
  forceLivenessMechanism = 'AUTO',
}: UseAdaptiveFaceDetectionProps = {}) => {
  const setIsWebGLSupported = useLivenessModelsStore(state => state.setIsWebGLSupported);

  const useClientDetection = useMemo(() => {
    return canUseClientSideDetection({
      forceLivenessMechanism,
      setIsWebGLSupported,
    });
  }, [forceLivenessMechanism, setIsWebGLSupported]);

  const clientDetection = useFaceDetection({
    videoRef,
    shouldPreload: useClientDetection,
  });

  const serverDetection = useServerFaceDetection({
    videoRef,
    shouldInitialize: !useClientDetection,
  });

  if (useClientDetection === null) {
    return {
      isModelLoading: true,
      facePosition: 'unknown' as FacePosition,
      startDetection: () => {},
      isCorrectPosition: () => false,
      isUsingServerDetection: null,
      isUsingClientDetection: undefined,
    };
  }

  return {
    isUsingClientDetection: useClientDetection,
    ...(useClientDetection ? clientDetection : serverDetection),
  };
};
